/**
 * Adjustments, write-offs, disposal and quarantine.
 *
 * Asking and deciding are different people's jobs. Anybody who handles stock
 * may record a loss or put a batch in quarantine; taking stock off the books
 * and putting quarantined stock back on sale wait for somebody who may
 * approve, and the decision carries their name.
 */

import { useState, type ReactNode } from 'react';
import { Plus } from 'lucide-react';
import { ADJUSTMENT_REASONS, ADJUSTMENT_REASON_LABELS, type AdjustmentReason } from '@cenox/shared';
import { useAuth } from '../../lib/auth';
import { money, todayIn } from '../../lib/format';
import { Badge, Button, Card, CardHeader, ErrorState, Input, Modal, Select, Spinner, Textarea } from '../../components/ui';
import { PageHeader } from '../../layouts/AppShell';
import { newKey, usePharmacy, usePharmacyWrite, type BatchRow, type ListOf } from './data';
import { ErrorLine, StatusBadge, Table, Td } from './parts';
import { LocationPicker, ProductPicker, StockTabs, useActionBranch } from './MedicinesPage';

interface AdjustmentRow {
  id: string;
  reference: string;
  branch_name: string;
  product_name: string;
  batch_number: string | null;
  quantity: number;
  quantity_before?: number | null;
  quantity_after?: number | null;
  reason: AdjustmentReason;
  note: string | null;
  status: string;
  value_at_cost: string | null;
  requested_at: string;
  requested_by: string | null;
  decided_at: string | null;
  decided_by: string | null;
  decision_note: string | null;
}

interface HoldRow {
  id: string;
  batch_number: string;
  expiry_date: string;
  product_name: string;
  branch_name: string | null;
  kind: string;
  reason: string;
  placed_at: string;
  placed_by: string | null;
  /** A delivery waiting for a pharmacist's check, not a quarantine somebody decided. */
  awaiting_check?: boolean;
}

export interface AdjustmentPreset {
  product?: { id: string; name: string } | undefined;
  batchId?: string | undefined;
  branchId?: string | undefined;
  quantity?: number | undefined;
  reason?: AdjustmentReason | undefined;
}

/** Reasons offered for each direction; "count correction" and "other" go either way. */
const INCREASE_REASONS: readonly AdjustmentReason[] = ['found', 'count_correction', 'other'];
const REDUCE_REASONS: readonly AdjustmentReason[] = ADJUSTMENT_REASONS.filter((r) => r !== 'found');

interface AdjustmentStart {
  productId: string;
  batchId: string | null;
  name: string;
  baseUnitName: string;
  classified: boolean;
  isMedicine: boolean;
  tracksBatches: boolean;
  onHand: number;
}

interface AdjustmentResult {
  status: string;
  reference: string;
  quantityBefore?: number | null;
  quantityAfter?: number | null;
  waitingFor?: string | null;
}

/**
 * Adjust stock: what is recorded now, the change, and what it will be, shown
 * and confirmed before anything is sent. The figure confirmed travels with
 * the request, so a sale in between refuses the change rather than landing
 * it on a different number. Every adjustment has a reason; nothing here makes
 * a sale or a payment.
 */
export function AdjustmentModal({ preset, onClose }: { preset: AdjustmentPreset; onClose: () => void }): ReactNode {
  const loc = useActionBranch();
  const branchId = preset.branchId ?? loc.branchId;
  const [product, setProduct] = useState(preset.product ?? null);
  const [batchId, setBatchId] = useState(preset.batchId ?? '');
  const [direction, setDirection] = useState<'increase' | 'reduce' | ''>(
    preset.quantity === undefined ? '' : preset.quantity > 0 ? 'increase' : 'reduce',
  );
  const [mode, setMode] = useState<'by' | 'to'>('by');
  const [amount, setAmount] = useState(preset.quantity === undefined ? '' : String(Math.abs(preset.quantity)));
  const [reason, setReason] = useState<AdjustmentReason | ''>(preset.reason ?? '');
  const [note, setNote] = useState('');
  const [reviewing, setReviewing] = useState(false);
  const [key] = useState(newKey);
  const batches = usePharmacy<ListOf<BatchRow>>('/batches', { productId: product?.id, branchId }, Boolean(product && branchId));
  const list = batches.data?.data ?? [];
  const startQuery = usePharmacy<AdjustmentStart>(
    '/adjustments/on-hand',
    { productId: product?.id, branchId, batchId: batchId || undefined },
    Boolean(product && branchId),
  );
  // A previous product's figure is never shown for this one.
  const start =
    startQuery.data && startQuery.data.productId === product?.id && (startQuery.data.batchId ?? '') === batchId ? startQuery.data : null;
  const save = usePharmacyWrite<Record<string, unknown>, AdjustmentResult>('post', '/adjustments');

  const whole = /^\d+$/.test(amount.trim());
  const entered = whole ? Number(amount) : NaN;
  const change = !start || !whole ? null : mode === 'to' ? entered - start.onHand : direction === 'reduce' ? -entered : direction === 'increase' ? entered : null;
  const after = start && change !== null ? start.onHand + change : null;
  const reasons = change === null || change === 0 ? [] : change > 0 ? INCREASE_REASONS : REDUCE_REASONS;
  const needBatch = Boolean(start?.tracksBatches) && !batchId;
  const problem = !product
    ? null
    : start && !start.classified
      ? `${start.name} is not classified yet. A pharmacist classifies it first; until then it holds no stock.`
      : needBatch
        ? 'Choose the batch: a batch-tracked item is adjusted batch by batch.'
        : !amount.trim()
          ? null
          : !whole
            ? 'A whole number of base units.'
            : change === 0
              ? 'That is what is recorded already: nothing to change.'
              : after !== null && after < 0
                ? `Only ${start!.onHand} on hand: it cannot go below zero.`
                : null;
  const ready =
    Boolean(product && branchId && start && start.classified && !needBatch && change !== null && change !== 0 && after !== null && after >= 0) &&
    (mode === 'to' || direction !== '') &&
    reason !== '' &&
    reasons.includes(reason as AdjustmentReason);
  const unit = start?.baseUnitName ?? 'unit';
  const done = save.data;

  return (
    <Modal open onClose={onClose} title="Adjust stock">
      <div className="space-y-3">
        {!preset.branchId && loc.ambiguous && <LocationPicker value={loc.branchId} onChange={loc.setBranchId} options={loc.options} />}
        {!done && !reviewing && (
          <>
            <ProductPicker value={product} onChange={(p) => { setProduct(p); setBatchId(''); }} />
            {product && start?.tracksBatches && (
              <Select
                label="Batch"
                value={batchId}
                placeholder={list.length ? 'Choose the batch' : 'No batch of it at this location'}
                onChange={(e) => setBatchId(e.target.value)}
                options={list.map((b) => ({ value: b.batchId, label: `${b.batchNumber} · exp ${b.expiryDate} · ${b.onHand} on hand` }))}
              />
            )}
            {start && start.classified && !needBatch && (
              <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">
                Recorded now: <strong className="tabular-nums">{start.onHand}</strong> {unit}
                {batchId ? ' in this batch' : ''} at this location
              </p>
            )}
            <div role="radiogroup" aria-label="How to adjust" className="flex flex-wrap gap-2 text-sm">
              {(
                [
                  ['increase', 'Increase'],
                  ['reduce', 'Reduce'],
                  ['to', 'Set the counted quantity'],
                ] as const
              ).map(([value, label]) => {
                const checked = value === 'to' ? mode === 'to' : mode === 'by' && direction === value;
                return (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={checked}
                    onClick={() => {
                      if (value === 'to') setMode('to');
                      else {
                        setMode('by');
                        setDirection(value);
                      }
                      setReason('');
                    }}
                    className={checked ? 'rounded-lg border border-brand-600 bg-brand-50 px-3 py-1.5 font-medium text-brand-800' : 'rounded-lg border border-slate-200 px-3 py-1.5 text-slate-700'}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input
                label={mode === 'to' ? `Counted quantity (${unit})` : `${direction === 'reduce' ? 'Reduce' : 'Increase'} by (${unit})`}
                inputMode="numeric"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
              <Select
                label="Reason"
                value={reason}
                placeholder={reasons.length ? 'Choose a reason' : 'Enter the change first'}
                onChange={(e) => setReason(e.target.value as AdjustmentReason)}
                options={reasons.map((r) => ({ value: r, label: ADJUSTMENT_REASON_LABELS[r] }))}
              />
            </div>
            <Textarea label="Notes (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
            {problem && <p className="text-sm font-medium text-amber-700">{problem}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={onClose}>Cancel</Button>
              <Button disabled={!ready} onClick={() => setReviewing(true)}>Review</Button>
            </div>
          </>
        )}
        {!done && reviewing && start && change !== null && after !== null && (
          <>
            <dl aria-label="Adjustment summary" className="grid grid-cols-3 gap-3 rounded-xl border border-slate-200 p-3 text-center">
              <div>
                <dt className="text-xs text-slate-500">Recorded now</dt>
                <dd className="text-lg font-semibold tabular-nums">{start.onHand}</dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">{change > 0 ? 'Increase by' : 'Reduce by'}</dt>
                <dd className="text-lg font-semibold tabular-nums">{Math.abs(change)}</dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">After</dt>
                <dd className="text-lg font-semibold tabular-nums">{after}</dd>
              </div>
            </dl>
            <p className="text-sm text-slate-600">
              {start.name}
              {batchId ? `, batch ${list.find((b) => b.batchId === batchId)?.batchNumber ?? ''}` : ''} · {unit} · {ADJUSTMENT_REASON_LABELS[reason as AdjustmentReason]}
              {note.trim() ? ` · ${note.trim()}` : ''}. No sale or payment is recorded.
            </p>
            {change > 0 && start.isMedicine && (
              <p className="text-xs text-slate-600">Stock a medicine gains is applied by a registered pharmacist; otherwise it waits for one.</p>
            )}
            <ErrorLine error={save.error} />
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => { save.reset(); setReviewing(false); void startQuery.refetch(); }}>Back</Button>
              <Button
                loading={save.isPending}
                onClick={() =>
                  save.mutate(
                    {
                      branchId,
                      productId: product!.id,
                      batchId: batchId || null,
                      quantity: change,
                      reason,
                      note: note.trim() || null,
                      expectedOnHand: start.onHand,
                      idempotencyKey: key,
                    },
                    { onError: () => void startQuery.refetch() },
                  )
                }
              >
                Confirm adjustment
              </Button>
            </div>
          </>
        )}
        {done && (
          <>
            <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
              {done.status === 'approved'
                ? `${done.reference} applied: ${done.quantityBefore} → ${done.quantityAfter} ${unit}.`
                : done.waitingFor === 'registered_pharmacist'
                  ? `${done.reference} is waiting for a registered pharmacist (Pharmacy → Settings → Pharmacists); stock is unchanged until one approves it.`
                  : `${done.reference} is waiting for approval; stock is unchanged until then.`}
            </p>
            <div className="flex justify-end">
              <Button onClick={onClose}>Close</Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

export function AdjustmentsPage(): ReactNode {
  const { can, business } = useAuth();
  const c = business?.currencyCode ?? 'NGN';
  const pending = usePharmacy<ListOf<AdjustmentRow>>('/adjustments', { status: 'pending' });
  const history = usePharmacy<ListOf<AdjustmentRow>>('/adjustments');
  const holds = usePharmacy<ListOf<HoldRow>>('/holds', { live: true });
  const decide = usePharmacyWrite<{ id: string; approve: boolean; note: string | null }>('post', (b) => `/adjustments/${b.id}/decide`);
  const [adding, setAdding] = useState(false);
  const [disposalFor, setDisposalFor] = useState<AdjustmentRow | null>(null);
  const [holdFor, setHoldFor] = useState<HoldRow | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [releasing, setReleasing] = useState<HoldRow[] | null>(null);
  const approver = can('pharmacy:stock_approve');
  const awaiting = (holds.data?.data ?? []).filter((h) => h.awaiting_check);
  const toggle = (id: string) =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const chosen = awaiting.filter((h) => checked.has(h.id));

  return (
    <div>
      <PageHeader
        title="Medicines & Stock"
        description="Damage, write-offs, corrections, disposal and quarantine — each with who asked and who decided"
        action={can('pharmacy:stock') ? <Button icon={<Plus className="size-4" />} onClick={() => setAdding(true)}>Adjust stock</Button> : undefined}
      />
      <StockTabs />
      <div className="space-y-5">
        <Card padded={false}>
          <div className="p-4 sm:p-5">
            <CardHeader title="Waiting for approval" subtitle="Nothing here has moved stock yet" />
          </div>
          {!pending.data ? (
            <Spinner />
          ) : pending.data.data.length === 0 ? (
            <p className="px-5 pb-6 text-sm text-slate-500">Nothing is waiting.</p>
          ) : (
            <Table head={['Ref', 'Item', 'Batch', 'Location', 'Qty', 'Reason', 'Asked by', '']} minWidth={880}>
              {pending.data.data.map((a) => (
                <tr key={a.id}>
                  <Td className="font-mono text-xs">{a.reference}</Td>
                  <Td>{a.product_name}</Td>
                  <Td className="font-mono text-xs">{a.batch_number ?? '—'}</Td>
                  <Td>{a.branch_name}</Td>
                  <Td className="tabular-nums">{a.quantity > 0 ? `+${a.quantity}` : a.quantity}</Td>
                  <Td>{ADJUSTMENT_REASON_LABELS[a.reason]}{a.note && <p className="text-xs text-slate-500">{a.note}</p>}</Td>
                  <Td>{a.requested_by}</Td>
                  <Td className="whitespace-nowrap text-right">
                    {approver && (
                      <>
                        <Button size="sm" loading={decide.isPending} onClick={() => decide.mutate({ id: a.id, approve: true, note: null })}>Approve</Button>{' '}
                        <Button size="sm" variant="ghost" onClick={() => decide.mutate({ id: a.id, approve: false, note: null })}>Reject</Button>
                      </>
                    )}
                  </Td>
                </tr>
              ))}
            </Table>
          )}
          <div className="px-4 pb-4"><ErrorLine error={decide.error} /></div>
        </Card>

        <Card padded={false}>
          <div className="p-4 sm:p-5">
            <CardHeader
              title="Quarantine and recall holds"
              subtitle="Held stock stays on the shelf and in the count, and is never sold. Recalled stock cannot be released while its recall is open."
              action={
                approver && awaiting.length > 0 ? (
                  <Button size="sm" disabled={chosen.length === 0} onClick={() => setReleasing(chosen)}>
                    Release {chosen.length} checked
                  </Button>
                ) : undefined
              }
            />
            {approver && awaiting.length > 0 && (
              <label className="mt-2 flex items-center gap-2 text-sm text-slate-600">
                <input
                  type="checkbox"
                  checked={chosen.length === awaiting.length}
                  onChange={(e) => setChecked(e.target.checked ? new Set(awaiting.map((h) => h.id)) : new Set())}
                />
                Select every batch awaiting a check ({awaiting.length}). A registered pharmacist releases them.
              </label>
            )}
          </div>
          {holds.isError ? (
            <div className="p-4"><ErrorState message="Could not load holds." /></div>
          ) : !holds.data ? (
            <Spinner />
          ) : holds.data.data.length === 0 ? (
            <p className="px-5 pb-6 text-sm text-slate-500">No stock is held.</p>
          ) : (
            <Table head={['', 'Item', 'Batch', 'Expiry', 'Where', 'Hold', 'Reason', 'Placed', '']} minWidth={900}>
              {holds.data.data.map((h) => (
                <tr key={h.id}>
                  <Td>
                    {approver && h.awaiting_check && (
                      <input type="checkbox" aria-label={`Select ${h.product_name} batch ${h.batch_number}`} checked={checked.has(h.id)} onChange={() => toggle(h.id)} />
                    )}
                  </Td>
                  <Td>{h.product_name}</Td>
                  <Td className="font-mono text-xs">{h.batch_number}</Td>
                  <Td className="tabular-nums">{h.expiry_date}</Td>
                  <Td>{h.branch_name ?? 'Every location'}</Td>
                  <Td>{h.awaiting_check ? <Badge tone="warning">awaiting check</Badge> : <StatusBadge status={h.kind} />}</Td>
                  <Td className="max-w-xs">{h.reason}</Td>
                  <Td className="text-xs">{h.placed_at.slice(0, 10)}<br />{h.placed_by}</Td>
                  <Td className="text-right">{approver && <Button size="sm" variant="secondary" onClick={() => setHoldFor(h)}>Decide</Button>}</Td>
                </tr>
              ))}
            </Table>
          )}
        </Card>

        <Card padded={false}>
          <div className="p-4 sm:p-5"><CardHeader title="History" /></div>
          {!history.data ? (
            <Spinner />
          ) : (
            <Table head={['Ref', 'Item', 'Batch', 'Location', 'Before', 'Change', 'After', 'Reason', 'Status', 'Value at cost', 'Asked · decided', '']} minWidth={1180}>
              {history.data.data.map((a) => (
                <tr key={a.id}>
                  <Td className="font-mono text-xs">{a.reference}</Td>
                  <Td>{a.product_name}</Td>
                  <Td className="font-mono text-xs">{a.batch_number ?? '—'}</Td>
                  <Td>{a.branch_name}</Td>
                  <Td className="tabular-nums">{a.quantity_before ?? '—'}</Td>
                  <Td className="tabular-nums">{a.quantity > 0 ? `+${a.quantity}` : a.quantity}</Td>
                  <Td className="tabular-nums">{a.quantity_after ?? '—'}</Td>
                  <Td>{ADJUSTMENT_REASON_LABELS[a.reason]}{a.note && <p className="text-xs text-slate-500">{a.note}</p>}</Td>
                  <Td><StatusBadge status={a.status} /></Td>
                  <Td className="tabular-nums">{a.value_at_cost ? money(a.value_at_cost, c) : '—'}</Td>
                  <Td className="text-xs">
                    {a.requested_by ?? '—'} · {new Date(a.requested_at).toLocaleString()}
                    {a.decided_by && <p>{a.decided_by} · {a.decided_at ? new Date(a.decided_at).toLocaleString() : ''}</p>}
                  </Td>
                  <Td className="text-right">
                    {approver && a.status === 'approved' && a.quantity < 0 && (
                      <Button size="sm" variant="ghost" onClick={() => setDisposalFor(a)}>Record disposal</Button>
                    )}
                  </Td>
                </tr>
              ))}
            </Table>
          )}
        </Card>
      </div>

      {adding && <AdjustmentModal preset={{}} onClose={() => setAdding(false)} />}
      {disposalFor && <DisposalModal adjustment={disposalFor} onClose={() => setDisposalFor(null)} />}
      {holdFor && <HoldDecisionModal hold={holdFor} onClose={() => setHoldFor(null)} />}
      {releasing && (
        <ReleaseChecksModal
          holds={releasing}
          onClose={() => setReleasing(null)}
          onDone={() => {
            setReleasing(null);
            setChecked(new Set());
          }}
        />
      )}
    </div>
  );
}

function DisposalModal({ adjustment, onClose }: { adjustment: AdjustmentRow; onClose: () => void }): ReactNode {
  const { business } = useAuth();
  const save = usePharmacyWrite<Record<string, unknown>>('post', `/adjustments/${adjustment.id}/disposal`);
  const [method, setMethod] = useState('');
  const [witness, setWitness] = useState('');
  const [on, setOn] = useState(todayIn(business?.timezone ?? 'UTC'));
  const [note, setNote] = useState('');
  return (
    <Modal open onClose={onClose} title={`Disposal for ${adjustment.reference}`}>
      <div className="space-y-3">
        <p className="text-sm text-slate-600">{adjustment.product_name} · {Math.abs(adjustment.quantity)} base units</p>
        <Input label="Method" placeholder="e.g. Handed to licensed waste contractor" value={method} onChange={(e) => setMethod(e.target.value)} />
        <Input label="Witnessed by" value={witness} onChange={(e) => setWitness(e.target.value)} />
        <Input label="Disposed on" type="date" value={on} onChange={(e) => setOn(e.target.value)} />
        <Textarea label="Note" value={note} onChange={(e) => setNote(e.target.value)} />
        <ErrorLine error={save.error} />
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            disabled={method.trim().length < 2 || !on}
            loading={save.isPending}
            onClick={() => save.mutate({ method: method.trim(), witnessedByName: witness.trim() || null, disposedOn: on, note: note.trim() || null }, { onSuccess: onClose })}
          >
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function ReleaseChecksModal({ holds, onClose, onDone }: { holds: HoldRow[]; onClose: () => void; onDone: () => void }): ReactNode {
  const save = usePharmacyWrite<{ holdIds: string[]; note: string }>('post', '/holds/release-checks');
  const [note, setNote] = useState('');
  return (
    <Modal open onClose={onClose} title={`Release ${holds.length} batch${holds.length === 1 ? '' : 'es'} for sale`}>
      <div className="space-y-3">
        <p className="text-sm text-slate-600">
          Each batch becomes saleable under your name. Release only what you have checked: the pack, its batch number and expiry, and how it is stored.
        </p>
        <ul className="max-h-48 overflow-y-auto text-sm text-slate-700">
          {holds.map((h) => (
            <li key={h.id}>{h.product_name} · {h.batch_number} · expires {h.expiry_date}</li>
          ))}
        </ul>
        <Textarea label="What you checked" required value={note} onChange={(e) => setNote(e.target.value)} />
        <ErrorLine error={save.error} />
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            disabled={note.trim().length < 5}
            loading={save.isPending}
            onClick={() => save.mutate({ holdIds: holds.map((h) => h.id), note: note.trim() }, { onSuccess: onDone })}
          >
            Release
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function HoldDecisionModal({ hold, onClose }: { hold: HoldRow; onClose: () => void }): ReactNode {
  const save = usePharmacyWrite<Record<string, unknown>>('post', `/holds/${hold.id}/decide`);
  const [decision, setDecision] = useState('release');
  const [note, setNote] = useState('');
  return (
    <Modal open onClose={onClose} title={`Decide ${hold.kind} on batch ${hold.batch_number}`}>
      <div className="space-y-3">
        {hold.kind === 'recall' && <Badge tone="danger">Recall: release is refused while the recall is open</Badge>}
        <Select
          label="Decision"
          value={decision}
          onChange={(e) => setDecision(e.target.value)}
          options={[
            { value: 'release', label: 'Release — fit to sell again' },
            { value: 'dispose', label: 'Dispose — write the held stock off' },
            { value: 'return_to_supplier', label: 'Returned to supplier (record the return first)' },
          ]}
        />
        <Textarea label="Why" required value={note} onChange={(e) => setNote(e.target.value)} />
        <ErrorLine error={save.error} />
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button disabled={note.trim().length < 3} loading={save.isPending} onClick={() => save.mutate({ decision, note: note.trim() }, { onSuccess: onClose })}>
            Decide
          </Button>
        </div>
      </div>
    </Modal>
  );
}
